import { t } from "i18next";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FieldPath, useFormContext, useWatch } from "react-hook-form";

// Interfaces
import { IRecipeForm } from "@/interfaces/recipe/recipe_form";
import { Macro } from "@/interfaces/nutrition/nutrition_builder";
import { IRecipeIngredient } from "@/interfaces/recipe/recipe_builder";

// Styles
import { TrashCan } from "@carbon/icons-react";

// Components
import { Box } from "@mui/material";
import UnitInput from "./unit_input";
import PortionInput from "./portion_field";
import { ColDef } from "@/components/datatable";
import TextInput from "@/components/text_input";
import IconButton from "@/components/icon_button";


interface _IngredientHeadersProps {
    readonly: boolean;
    onRemoveIngredient: (index: number) => void;
}

const useIngredientHeaders = ({
    readonly,
    onRemoveIngredient
}: _IngredientHeadersProps): ColDef<IRecipeIngredient>[] => {

    const { t } = useTranslation();
    const { control } = useFormContext<IRecipeForm>();

    const handleDeleteIngredient = (index: number) => {
        onRemoveIngredient(index);
    }

    const headers: ColDef<IRecipeIngredient>[] = [
        {
            field: 'name',
            headerName: t('components.nutritionBuilder.headers.food'),
            width: '20%',
            render: (_, i) => <TextInput
                name={`ingredients.${i}.name`}
                control={control}
                disabled
                gutter="0"
                hideErrorMessage
                />
        },
        {
            field: 'portion',
            headerName: t('components.nutritionBuilder.headers.portion'),
            width: '7.5%',
            render: (_, i) => <PortionInput ingredientIndex={i} readonly={readonly} />
        },
        {
            field: 'unit',
            headerName: t('components.nutritionBuilder.headers.unit'),
            width: '15%',
            render: (ingredient, i) => <UnitInput
                ingredientIndex={i}
                units={ingredient.units}
                readonly={readonly}
                />
        },
        {
            field: 'protein',
            headerName: t('components.nutritionBuilder.headers.protein'),
            width: '10%',
            render: (_, index) => <FoodMacro name={`ingredients.${index}`} macro='protein' />
        },
        {
            field: 'carbs',
            headerName: t('components.nutritionBuilder.headers.carbs'),
            width: '10%',
            render: (_, index) => <FoodMacro name={`ingredients.${index}`} macro='carbs' />
        },
        {
            field: 'fat',
            headerName: t('components.nutritionBuilder.headers.fat'),
            width: '10%',
            render: (_, index) => <FoodMacro name={`ingredients.${index}`} macro='fat'  />
        },
        {
            field: 'fibre',
            headerName: t('components.nutritionBuilder.headers.fibre'),
            width: '10%',
            render: (_, index) => <FoodMacro name={`ingredients.${index}`} macro='fibre'  />
        },
        {
            field: 'calories',
            headerName: t('components.nutritionBuilder.headers.calories'),
            width: '10%',
            render: (_, index) => <FoodMacro name={`ingredients.${index}`} macro='calories'  />
        },
        {
            field: 'actions',
            headerName: '',
            width: '6%',
            render: (_, i) => 
                <Box display="flex" justifyContent="flex-end">
                    <IconButton
                        kind="danger-ghost"
                        size="small"
                        icon={<TrashCan />}
                        disabled={readonly}
                        onClick={() => handleDeleteIngredient(i)}
                    />
                </Box>
        }
    ];

    return headers;
}

export default useIngredientHeaders;


interface _FoodMacroProps {
    name: FieldPath<any>;
    macro: Macro;
}

const FoodMacro: FC<_FoodMacroProps> = ({
    name,
    macro,
}) => {

    const ingredient = useWatch({name}) as IRecipeIngredient;

    const value: string = useMemo(() => {
        const res = ((ingredient.macros[macro] as number) / 100) * (ingredient.amount * ingredient.unit.weight)
        return Number(res).toLocaleString(undefined, {maximumFractionDigits: 1});
    }, [ingredient, macro])

    return (
        !macro.endsWith('calories') ? 
            <span className="body-02-compact">{`${value} g`}</span> :
            <span className="body-02-compact">{`${value} ${t('components.nutritionBuilder.macros.kcal')}`}</span>
    );
    
}